// Internal chrome for the selection feature (checkboxes + selection bar) -
// not barrel-exported; the public surface is `DataTableSelection`.

import type { ReactNode } from 'react';
import { dataTableButtonClasses } from './buttonStyles';
import type { DataTableButtonAction } from './types';

const BOX_BASE =
    'w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center transition-all duration-200';

function CheckIcon() {
    return (
        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none" aria-hidden="true">
            <path
                d="M1 4L3.5 6.5L9 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function DashIcon() {
    return (
        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 2" fill="none" aria-hidden="true">
            <path d="M1 1H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

export type SelectAllState = 'all' | 'some' | 'none';

export function SelectAllCheckbox({
    state,
    onToggle
}: {
    state: SelectAllState;
    onToggle: () => void;
}) {
    return (
        <div className="flex items-center justify-center">
            <button
                type="button"
                role="checkbox"
                aria-checked={state === 'all' ? true : state === 'some' ? 'mixed' : false}
                aria-label="Select all rows"
                onClick={onToggle}
                className={`${BOX_BASE} ${
                    state === 'all'
                        ? 'bg-gray-800 border-gray-800'
                        : state === 'some'
                          ? 'bg-gray-300 border-gray-400'
                          : 'border-gray-300 hover:border-gray-500 bg-white'
                }`}
            >
                {state === 'all' ? <CheckIcon /> : state === 'some' ? <DashIcon /> : null}
            </button>
        </div>
    );
}

interface SelectionBarProps {
    count: number;
    notice?: ReactNode;
    actions?: readonly DataTableButtonAction[];
}

/** The in-card bar shown while selection is enabled: "N selected", the
 * caller's notice and action buttons (see `DataTableSelectionBase.actions`). */
export function SelectionBar({ count, notice, actions }: SelectionBarProps) {
    const shownActions = actions ?? [];
    const hasTrailing = Boolean(notice) || shownActions.length > 0;
    return (
        // aria-live so assistive tech hears the count move as rows toggle.
        <div
            aria-live="polite"
            className="px-6 py-2.5 border-b border-gray-200 bg-gray-50/80 flex items-center gap-3"
        >
            {count === 0 ? (
                (notice ?? (
                    <span className="text-sm text-gray-500">
                        Select rows to perform bulk actions
                    </span>
                ))
            ) : (
                <>
                    <span className="text-sm font-medium text-gray-700">{count} selected</span>
                    {hasTrailing && <div className="w-px h-4 bg-gray-300" />}
                    {notice}
                    {shownActions.map((action) => (
                        <button
                            key={action.key ?? action.label}
                            type="button"
                            onClick={action.onClick}
                            disabled={action.disabled}
                            title={action.title}
                            className={
                                action.className ?? dataTableButtonClasses(action.variant, 'sm')
                            }
                        >
                            {action.icon}
                            {action.label}
                        </button>
                    ))}
                </>
            )}
        </div>
    );
}

export function RowCheckbox({ checked, onToggle }: { checked: boolean; onToggle: () => void }) {
    return (
        <div className="flex items-center justify-center">
            <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={checked ? 'Deselect row' : 'Select row'}
                onClick={onToggle}
                className={`${BOX_BASE} ${
                    checked
                        ? 'bg-gray-800 border-gray-800 scale-110'
                        : 'border-gray-300 hover:border-gray-500 bg-white hover:shadow-sm'
                }`}
            >
                {checked && <CheckIcon />}
            </button>
        </div>
    );
}
